import React, { ReactElement, useCallback } from 'react';
import { MCheckbox, Status } from '@Components/Base';
import RequestType from '@Types/common/RequestTypes';
import { useUserPermissions } from '@Hooks/index';
import { RequestListContext } from '@Components/types/RequestListContext';
import {
  ApprovalsTasksRequisitionResponse,
  ApprovalsTasksResponse,
} from '@Types/api/approvalsList';
import { AttendanceProfileResponse } from '@Types/api/profile/AttendanceProfileList';
import { parseDateTime } from '@Utils/transformDateTime';
import {
  TableManageRequestsActions,
  TableTaskListActions,
} from '../../tableActions';
import styles from '../request-list-mobile.module.css';

interface CardProps {
  requestNo: number;
  status: string;
  requestType: RequestType;
  requestTypeCategory: string;
  items: Array<{ label: string; value: string }>;
  recordItem:
    | ApprovalsTasksResponse
    | ApprovalsTasksRequisitionResponse
    | AttendanceProfileResponse;
  showAction: boolean;
  context: RequestListContext;
  showCheckbox: boolean;
  isSelected: boolean;
  ownActions?: ReactElement | null;
  onEdit: () => void;
  onSubmit: () => void;
  onView: () => void;
  onDelete: () => void;
  onCancel: () => void;
  onApprovalHistory?: (requestNo: string, isIt?: boolean | undefined) => void;
  onCopy?: () => void;
  onApproveReject?: () => void;
  onSelectTask?: () => void;
}

const Card = ({
  requestNo,
  status,
  requestType,
  requestTypeCategory,
  items,
  showAction,
  recordItem,
  context,
  showCheckbox,
  isSelected,
  ownActions,
  onEdit,
  onSubmit,
  onView,
  onCancel,
  onCopy,
  onDelete,
  onApprovalHistory,
  onApproveReject,
  onSelectTask,
}: CardProps): JSX.Element | null => {
  const permissions = useUserPermissions();
  let elementWithProps;

  if (ownActions) {
    elementWithProps = React.cloneElement(ownActions, {
      record: {
        ...recordItem,
        date: parseDateTime((recordItem as AttendanceProfileResponse).AttDate),
      },
    });
  }

  const renderActions = useCallback(() => {
    if (!showAction) return null;

    if (context === 'approvals') {
      return (
        <TableTaskListActions
          record={
            recordItem as
              | ApprovalsTasksResponse
              | ApprovalsTasksRequisitionResponse
          }
          context="mobile"
          onApprovalHistory={onApprovalHistory}
          onApproveReject={onApproveReject}
        />
      );
    }

    return (
      <TableManageRequestsActions
        requestType={requestType}
        requestNo={requestNo}
        status={status}
        permissions={permissions}
        onEdit={onEdit}
        onSubmit={onSubmit}
        onView={onView}
        onCancel={onCancel}
        onCopy={onCopy}
        onDelete={onDelete}
        onApprovalHistory={() => onApprovalHistory?.(String(requestNo))}
      />
    );
  }, [showAction, context]);

  return (
    <div className={styles.card}>
      <div className={styles.cardManageSection}>
        {showCheckbox && (
          <div className={styles.cardCheckbox}>
            <MCheckbox onClick={onSelectTask} checked={isSelected} />
          </div>
        )}
        <div className={styles.cardType}>{requestTypeCategory}</div>
        {!elementWithProps ? (
          <div className={styles.cardWrap}>
            <div className={styles.cardStatus}>
              <Status text={status} />
            </div>
            {renderActions()}
          </div>
        ) : (
          <div>{elementWithProps}</div>
        )}
      </div>
      <div className={styles.cardInfoSection}>
        {items?.map((item) => (
          <div key={item.label} className={styles.cardInfoType}>
            <span>{item.label}</span>
            <span
              className={item?.value?.length > 10 ? styles.textOverflow : ''}
            >
              {item?.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Card;
